import {AbstractPlayableContent} from "../playable/models/AbstractPlayableContent";
import {getMasterType, getScoreDifferenceMap} from "./MasteriesUtils";
import {BackEndService} from "../../utils/BackEndService";
import {userService} from "../user/UserService";
import { playerConfig } from "../player/models/PlayerConfig";

// Keep console.log for potential futur debug, keep it at least until 20 feb 2023 to ensure all is OK

/*
 Could separate Score From Masteries
 */
export class MasteriesService {
  constructor() {
    userService.on('userService_firstLoaded', () => {
      // 1st get local score (and local diff)
      // 2nd get distant score and add unknown musics to list
      //  // That cause two issues :
      //  // - We are not handling multiple devices
      //  // - New musics won't have their animation
      this.initScores().then(() => {
        this.scoreHasBeenInitialized = true;
        this.updateCatalogScoreUsingDistantScores().then();
      });
    })
  }

  // TODO: add models | might add cast to score
  private scoreHasBeenInitialized = false;
  catalogScore = {
    lastUpdateDate: null as any,
    challenges: [] as any[],
    versions: [] as any[],
  };
  catalogDiff = {
    challenges: [] as any[],
    versions: [] as any[],
  };

  sessionScore = {
    challenges: [] as any[],
    versions: [] as any[],
  };
  sessionScoreDiff = {
    challenges: [] as any[],
    versions: [] as any[],
  }

  /**
   * Made for update periodically (for instance each time user open catalog)
   */
  public async updateCatalogScoreUsingDistantScores() {
    if (!this.scoreHasBeenInitialized) {
      // Previously using a time check to reload not to often but seems to be useless now
      return;
    }

    const newScores = await this.getDistantScores();
    if (this.catalogScore.challenges.length > 0) {
      this.catalogDiff.challenges = getScoreDifferenceMap(newScores.challenges, this.catalogScore.challenges,  1)
    }
    if (this.catalogScore.versions.length > 0) {
      this.catalogDiff.versions = getScoreDifferenceMap(newScores.versions, this.catalogScore.versions,  1)
    }

    this.persistLocalScores();
  }

  public async updateLocalScoreFor(scoreType: 'challenges' | 'versions', id: number, score: number) {
    // Remove entry from catalogDiff
    this.catalogDiff[scoreType] = this.catalogDiff[scoreType].filter((entry) => {
      return entry.id !== id
    })

    // Update local score
    const catalogEntry = this.catalogScore[scoreType].find((entry) => {
      return entry.id === id
    })
    if (catalogEntry) {
      catalogEntry.score = score;
    }
    else {
      this.catalogScore[scoreType].push({
        id: id, score: score, discovered_at: null, last_opened_at: null, class_status: null, piano_class_progress: null
      })
    }

    this.persistLocalScores();
  }

  /**
   * Made for update on each score update
   */

  public async initSessionScoreUsingDistantScoreIfNeeded() {
    // console.log('initSessionScoreUsingDistantScoreIfNeeded');
    // console.log(this.sessionScore);
    if (this.sessionScore?.versions?.length === 0 ?? false) {
      // console.log('in init');
      // console.log(this.sessionScore);
      this.sessionScore = await this.getDistantScores();
    }
  }

  // Min diff to display changes
  readonly CHALLENGE_MIN_DIFF = 3;
  readonly VERSION_MIN_DIFF = 3;

  public async updateSessionScoreDiffAndSessionScore() {
    await this.updateSessionScoreDiffUsingDistantScores();
    this.updateSessionScoreUsingScoreDiff();
  }

  private async updateSessionScoreDiffUsingDistantScores() {
    const newScores = await this.getDistantScores();
    this.sessionScoreDiff.challenges = getScoreDifferenceMap(newScores.challenges, this.sessionScore.challenges, this.CHALLENGE_MIN_DIFF)
    this.sessionScoreDiff.versions = getScoreDifferenceMap(newScores.versions, this.sessionScore.versions, this.VERSION_MIN_DIFF)
  }
  private updateSessionScoreUsingScoreDiff() {
    this.sessionScoreDiff.versions.forEach((sessionScoreDiff: any) => {
      // console.log(this.sessionScore);
      const versionScore = this.sessionScore.versions.find((versionScore) => versionScore.id === sessionScoreDiff.id)
      // console.log(versionScore);
      if (versionScore) {
        versionScore.score = sessionScoreDiff.score
      }
      else {
        // console.log('versionScore not found pushing sessionScoreDiff')
        // console.log(sessionScoreDiff)
        this.sessionScore.versions.push(sessionScoreDiff)
      }
    })

    this.sessionScoreDiff.challenges.forEach((sessionScoreDiff: any) => {
      const challengeScore = this.sessionScore.challenges.find((challengeScore) => challengeScore.id === sessionScoreDiff.id)
      // console.log(challengeScore);
      if (challengeScore) {
        challengeScore.score = sessionScoreDiff.score
      }
      else {
        // console.log('challengeScore not found pushing sessionScoreDiff')
        // console.log(sessionScoreDiff)
        this.sessionScore.challenges.push(sessionScoreDiff)
      }
    })
  }

  /* Persistence */

  private async initScores() {
    await this.getLocalScores();

    // Add distant score that are not present in catalogScore
    const newScores = await this.getDistantScores();
    this.catalogScore.challenges = this.catalogScore.challenges.concat(
      newScores.challenges.filter((challengeScore: any) => {
        return !this.catalogScore.challenges.find((challenge) => challenge.id === challengeScore.id)
      })
    )
    this.catalogScore.versions = this.catalogScore.versions.concat(
      newScores.versions.filter((versionScore: any) => {
        return !this.catalogScore.versions.find((version) => version.id === versionScore.id)
      })
    )
    this.catalogScore.lastUpdateDate = Date.now();

    this.persistLocalScores();
  }
  private getDistantScores() {
    return BackEndService.sendGetRequest('scores').then((response) => {
      response.content.lastUpdateDate = Date.now();
      return response.content;
    })
  }

  private getLocalScores() {
    const scoresJson = localStorage.getItem('scores_'+ userService.user.id);
    if (scoresJson) {
      this.catalogScore = JSON.parse(scoresJson);
    }
  }
  private persistLocalScores() {
    localStorage.setItem('scores_'+ userService.user.id, JSON.stringify(this.catalogScore))
  }

  /** ************* */
  /* Validation Api */
  /** ************* */
  public static validateMasteries(playableContent: AbstractPlayableContent) {
    const masterType = getMasterType(playerConfig.state.playerView, playerConfig.state.visualParameters);

    if (masterType !== null) {
      const sectionIds = [] as number[];

      switch (playableContent.playableType) {
        case 'section':
          sectionIds.push(playableContent.id);
          break;

        case 'part':
        case 'challenge':
          sectionIds.push(...new Set<number>(playableContent.baseModel.section_timeline));
          break;

        default:
          console.error('Masteries not supported for ' + playableContent.playableType);
          return Promise.reject();
      }

      return MasteriesService.chainMasteriesValidationForSections(sectionIds, masterType)
    }
    return Promise.resolve();
  }
  private static async chainMasteriesValidationForSections(sectionIds: number[], masterType: string) {
    BackEndService.sendPostRequest('master/' + sectionIds.shift(), {master_type: masterType}).then(() => {
      if (sectionIds.length > 0) {
        MasteriesService.chainMasteriesValidationForSections(sectionIds, masterType)
      }
    })
  }
}

export const masteriesService = new MasteriesService();
